// ============================================================
// TMR Portal Admin — mandar o contrato enviado para outro telefone ou e-mail
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, Segmented } from '../../components/primitives';
import { TextField } from '../../components/fields';
import { Notice } from '../../components/Notice';
import { CopyableText } from '../../components/CopyableText';
import { Icons } from '../../lib/icons';
import { useChangeSignatory } from '../../data/contracts/queries';
import type {
  ChangeSignatoryResponse,
  ContractRecord,
  SignatureDeliveryChannel,
} from '../../data/contracts/contract';
import { ContractDialog } from './ContractDialog';

/** Props for {@link ChangeSignatoryModal}. */
interface ChangeSignatoryModalProps {
  /** The contract out for signature — its current channel and contact start the form. */
  contract: ContractRecord;
  onClose: () => void;
}

/**
 * The way out of a send that went to the wrong phone or e-mail (02 §7).
 *
 * **Same document, new signatory.** The provider takes the representative off the document and puts them
 * back at the corrected contact, so the link that went to the wrong number stops working and no second
 * document is raised. The TMR's counter-signature is untouched.
 *
 * **A one-off for this send.** The Empresa's cadastre is not changed — correcting the representative for
 * good is the party edit's job, and the form says so rather than leaving the operator to wonder.
 *
 * The contact is required here even though the endpoint would fall back to the cadastre's: the operator
 * opened this to type a corrected one, and an empty field that silently sends to the old one would repeat
 * the mistake it exists to fix.
 */
export function ChangeSignatoryModal({ contract, onClose }: ChangeSignatoryModalProps) {
  const [channel, setChannel] = useState<SignatureDeliveryChannel>(
    contract.signatureDeliveryChannel ?? 'WhatsApp',
  );
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<ChangeSignatoryResponse | null>(null);
  const changeSignatory = useChangeSignatory();

  const error = changeSignatory.error instanceof ApiError ? changeSignatory.error : null;
  const contact = channel === 'WhatsApp' ? phone : email;
  const ready = contact.trim().length > 0 && !changeSignatory.isPending;

  const submit = () => {
    changeSignatory.mutate(
      {
        contractId: contract.id,
        channel,
        ...(channel === 'WhatsApp' ? { phone: phone.trim() } : { email: email.trim() }),
      },
      // It does not close itself: the new link comes back here, and the operator may want it.
      { onSuccess: setDone },
    );
  };

  return (
    <ContractDialog
      eyebrow="Trocar contato"
      title={done ? 'Enviado para o novo contato' : 'Enviar para outro telefone ou e-mail'}
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" onClick={onClose}>
            {done ? 'Concluir' : 'Fechar'}
          </Btn>
          {!done && (
            <Btn
              variant="primary"
              icon={changeSignatory.isPending ? undefined : Icons.send}
              onClick={ready ? submit : undefined}
              disabled={!ready}
            >
              {changeSignatory.isPending
                ? 'Enviando…'
                : changeSignatory.error
                  ? 'Tentar novamente'
                  : 'Trocar e enviar'}
            </Btn>
          )}
        </>
      }
    >
      {done ? (
        <>
          <Notice tone="success">
            O mesmo documento foi enviado{' '}
            {done.contract.signatureDeliveryChannel === 'WhatsApp' ? 'por WhatsApp' : 'por e-mail'}
            {done.contract.signatureDeliveryTarget ? (
              <>
                {' '}
                para <strong>{done.contract.signatureDeliveryTarget}</strong>
              </>
            ) : null}
            . O link enviado ao contato anterior não funciona mais.
          </Notice>
          {done.signatureUrl && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginBottom: 4 }}>Novo link de assinatura</div>
              <CopyableText
                value={done.signatureUrl}
                style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere', display: 'inline-block' }}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <p style={{ margin: '0 0 14px' }}>
            O representante legal recebe <strong>o mesmo documento</strong> no contato abaixo, e o link
            enviado antes deixa de funcionar.
            {contract.signatureDeliveryTarget ? (
              <>
                {' '}
                Hoje ele está com <strong>{contract.signatureDeliveryTarget}</strong>.
              </>
            ) : null}
          </p>

          <div className="pa-form-label" style={{ marginBottom: 8 }}>
            Como enviar
          </div>
          <Segmented<SignatureDeliveryChannel>
            size="sm"
            value={channel}
            onChange={setChannel}
            options={[
              { value: 'WhatsApp', label: 'WhatsApp' },
              { value: 'Email', label: 'E-mail' },
            ]}
          />

          <div style={{ marginTop: 14 }}>
            {channel === 'WhatsApp' ? (
              <TextField
                label="Telefone"
                value={phone}
                onChange={setPhone}
                placeholder="(11) 98888-7777"
                inputMode="tel"
                maxLength={20}
                required
              />
            ) : (
              <TextField
                label="E-mail"
                value={email}
                onChange={setEmail}
                placeholder="representante@clinica.com.br"
                inputMode="email"
                type="email"
                maxLength={200}
                required
              />
            )}
          </div>

          <Notice tone="neutral">
            Vale <strong>só para este envio</strong> — o cadastro da empresa não muda. Para corrigir o contato
            do representante de vez, edite a Parte no cadastro.
          </Notice>
        </>
      )}

      {error && (
        <Notice tone="danger">
          {error.code === 'validation'
            ? 'O contato informado não pode ser usado.'
            : error.code === 'conflict'
              ? 'Este documento não aceita mais a troca de contato.'
              : // Not "the old contact still works": a refusal after the old signatory was removed leaves the
                // document with nobody on it. Repeating is what finishes it, and it is safe.
                'O provedor de assinatura recusou a troca e ela não foi concluída. Tente de novo — repetir é seguro.'}{' '}
          {error.detail}
        </Notice>
      )}
    </ContractDialog>
  );
}
